import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useId, type ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, type TestIdProps} from 'shared/web';

export type SelectableCardProps = TestIdProps & {
  /** Accessible name of the underlying radio/checkbox. */
  title: string;
  /** Rendered instead of the plain title when richer content is needed. */
  heading?: ReactNode;
  description?: ReactNode;
  /** Right-aligned value slot (price, "Included", …). */
  trailing?: ReactNode;
  /** Secondary line under the description, typically a rule or reason. */
  footer?: ReactNode;
  /** Optional leading visual (swatch, icon). */
  leading?: ReactNode;
  control: 'radio' | 'checkbox';
  name?: string;
  isSelected: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
  isRequired?: boolean;
  /** Tooltip / accessible description for disabled controls. */
  reason?: string;
  onSelect: VoidFunction;
};

const TRANSITION = '140ms cubic-bezier(0.25, 1, 0.5, 1)';

const Root = styled.label`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${({theme}) => theme.getSize(3)};
  width: 100%;
  padding: ${({theme}) => `${theme.getSize(3)} ${theme.getSize(4)}`};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  background-color: ${({theme}) => theme.colors.general.white};
  cursor: pointer;
  user-select: none;
  transition:
    border-color ${TRANSITION},
    background-color ${TRANSITION},
    box-shadow ${TRANSITION};

  &:hover {
    border-color: ${({theme}) => theme.colors.palettes.neutral[70][100]};
    box-shadow: ${({theme}) => theme.shadows.elevation_1};
  }

  &[data-selected='true'] {
    border-color: ${({theme}) => theme.colors.general.accent};
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
    box-shadow: inset 0 0 0 1px ${({theme}) => theme.colors.general.accent};
  }

  &[data-selected='true']:hover {
    background-color: ${({theme}) => theme.colors.palettes.blue[20][100]};
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
    background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
    border-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
    box-shadow: none;
  }

  &[data-locked='true'] {
    cursor: default;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const IndicatorSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const Input = styled.input`
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: inherit;

  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const Indicator = styled.span<{$shape: 'radio' | 'checkbox'}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(5)};
  height: ${({theme}) => theme.getSize(5)};
  border: 1px solid ${({theme}) => theme.colors.palettes.neutral[200][100]};
  border-radius: ${({theme, $shape}) =>
    $shape === 'radio' ? theme.radii.circular : theme.radii.small};
  background-color: ${({theme}) => theme.colors.general.white};
  color: ${({theme}) => theme.colors.general.white};
  transition:
    border-color ${TRANSITION},
    background-color ${TRANSITION};

  &[data-checked='true'] {
    border-color: ${({theme}) => theme.colors.general.accent};
    background-color: ${({theme}) => theme.colors.general.accent};
  }

  &[data-checked='true'][data-shape='radio'] {
    border-width: ${({theme}) => theme.getSize(1)};
    background-color: ${({theme}) => theme.colors.general.white};
  }

  &[data-disabled='true'] {
    opacity: 0.45;
  }
`;

const Content = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(1)};
`;

const Footer = styled.span`
  display: block;
  position: relative;
  z-index: 2;
`;

export function SelectableCard(props: SelectableCardProps) {
  const id = useId();
  const isDisabled = Boolean(props.isDisabled);
  const isLocked = Boolean(props.isLocked);
  const isInert = isDisabled || isLocked;
  const descriptionId = isNotNil(props.reason) ? `${id}-reason` : undefined;

  return (
    <Root
      htmlFor={id}
      data-selected={props.isSelected}
      data-disabled={isDisabled}
      data-locked={isLocked}
      data-testid={suffixTestId('selectableCard', props)}
    >
      <Box paddingTop={props.leading ? 0 : 1} flexShrink={0}>
        <IndicatorSlot>
          <Input
            id={id}
            type={props.control}
            name={props.name}
            checked={props.isSelected}
            disabled={isInert}
            required={props.isRequired}
            aria-label={props.title}
            aria-describedby={descriptionId}
            onChange={() => {
              if (!isInert) {
                props.onSelect();
              }
            }}
            data-testid={suffixTestId('selectableCardInput', props)}
          />
          <Indicator
            $shape={props.control}
            data-shape={props.control}
            data-checked={props.isSelected}
            data-disabled={isInert}
            aria-hidden
          >
            <Show when={props.isSelected && props.control === 'checkbox'}>
              <Icon value="toggle/tick" size={3} color="general.white" />
            </Show>
          </Indicator>
        </IndicatorSlot>
      </Box>
      <Show when={props.leading}>
        <Box flexShrink={0}>{props.leading}</Box>
      </Show>
      <Content>
        <HStack justify="space-between" align="flex-start" spacing={4}>
          <VStack spacing={1} grow={1} minWidth={0}>
            {props.heading ?? (
              <Text alternative color={isDisabled ? 'secondary' : 'primary'}>
                {props.title}
              </Text>
            )}
            <Show when={isNotNilOrEmpty(props.description)}>
              <Text size="small" color="secondary">
                {props.description}
              </Text>
            </Show>
          </VStack>
          <Show when={props.trailing}>
            <Box flexShrink={0}>{props.trailing}</Box>
          </Show>
        </HStack>
        <Show when={isNotNilOrEmpty(props.footer) || isNotNilOrEmpty(props.reason)}>
          <Footer id={descriptionId}>{props.footer ?? props.reason}</Footer>
        </Show>
      </Content>
    </Root>
  );
}
